import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { createProjectsApi } from '@shared/api/projects';
import { parseProjectFile, parseBacklogKanban, parseTasks, parseSprints, serializeFrontMatter, serializeTask, } from '@shared/markdown/parser';
import { serializeBacklog, serializeSprints } from '@shared/markdown/save';
import { ensureDefaultStates } from '@shared/core/defaults';
import { generateChangeLogEntry } from '@shared/task/changelog';
import { captureMetadata } from '@shared/metadata/capture';
import { useAuthStore } from './auth-store';
const seedProjects = [
    {
        project_id: 'PRJ-001',
        name: 'Website Redesign',
        workflow: 'kanban',
        default_branch: 'main',
        created: '2026-06-01T09:00:00Z',
        updated: '2026-06-11T14:30:00Z',
        status: 'active',
        tags: ['design', 'frontend', 'website'],
        config: { wip_limit: 5, timezone: 'America/New_York' },
    },
    {
        project_id: 'PRJ-002',
        name: 'Mobile App MVP',
        workflow: 'scrum',
        default_branch: 'main',
        created: '2026-06-01T09:00:00Z',
        updated: '2026-06-11T14:30:00Z',
        status: 'active',
        tags: ['mobile', 'ios', 'android', 'mvp'],
        config: { sprint_duration: 14, wip_limit: 3, story_point_scale: [1, 2, 3, 5, 8, 13], timezone: 'America/Los_Angeles' },
    },
];
const seedTasks = [
    // PRJ-001 Kanban tasks
    {
        id: 'TASK-001',
        projectId: 'PRJ-001',
        title: 'Design new homepage mockup',
        status: 'done',
        priority: 'high',
        assignee: '@maya',
        storyPoints: 5,
        labels: ['design', 'homepage'],
        created: '2026-06-01T09:00:00Z',
        updated: '2026-06-05T16:00:00Z',
        lastUpdatedBy: 'maya',
        started: '2026-06-01T09:30:00Z',
        description: 'Create high-fidelity mockups for the new homepage including hero section, feature highlights, testimonials, and footer.',
        acceptanceCriteria: [
            { text: 'Hero section with CTA', completed: true },
            { text: 'Feature grid (6 items)', completed: true },
            { text: 'Testimonials carousel', completed: true },
            { text: 'Footer with links', completed: true },
            { text: 'Mobile responsive versions', completed: true },
        ],
        timeLog: [],
        comments: [],
    },
    {
        id: 'TASK-002',
        projectId: 'PRJ-001',
        title: 'Implement responsive navigation',
        status: 'done',
        priority: 'high',
        assignee: '@alex',
        storyPoints: 3,
        labels: ['frontend', 'navigation'],
        created: '2026-06-02T10:00:00Z',
        updated: '2026-06-04T17:00:00Z',
        lastUpdatedBy: 'alex',
        started: '2026-06-02T10:30:00Z',
        description: 'Build responsive navigation with hamburger menu for mobile, dropdown for desktop, and smooth transitions.',
        acceptanceCriteria: [
            { text: 'Desktop navigation with dropdowns', completed: true },
            { text: 'Mobile hamburger menu', completed: true },
            { text: 'Smooth transition animations', completed: true },
            { text: 'Keyboard accessible', completed: true },
            { text: 'ARIA labels', completed: true },
        ],
        timeLog: [],
        comments: [],
    },
    {
        id: 'TASK-003',
        projectId: 'PRJ-001',
        title: 'Create component library',
        status: 'in-progress',
        priority: 'high',
        assignee: '@maya',
        storyPoints: 8,
        labels: ['design', 'components'],
        created: '2026-06-03T09:00:00Z',
        updated: '2026-06-10T14:00:00Z',
        lastUpdatedBy: 'maya',
        started: '2026-06-03T09:30:00Z',
        description: 'Design and document reusable UI components: buttons, cards, inputs, modals, tooltips, badges, and alert boxes.',
        acceptanceCriteria: [
            { text: 'Button variants (primary, secondary, ghost)', completed: true },
            { text: 'Card component', completed: true },
            { text: 'Input fields with validation', completed: true },
            { text: 'Modal component', completed: false },
            { text: 'Tooltip component', completed: false },
            { text: 'Badge component', completed: false },
            { text: 'Alert component', completed: false },
            { text: 'Storybook documentation', completed: false },
        ],
        timeLog: [],
        comments: [],
    },
    {
        id: 'TASK-004',
        projectId: 'PRJ-001',
        title: 'Build homepage layout',
        status: 'in-progress',
        priority: 'high',
        assignee: '@alex',
        storyPoints: 5,
        labels: ['frontend', 'homepage'],
        created: '2026-06-05T10:00:00Z',
        updated: '2026-06-11T09:00:00Z',
        lastUpdatedBy: 'alex',
        started: '2026-06-05T10:30:00Z',
        description: 'Implement the homepage layout based on the approved mockups using the component library.',
        acceptanceCriteria: [
            { text: 'Hero section implementation', completed: true },
            { text: 'Feature grid', completed: true },
            { text: 'Testimonials section', completed: false },
            { text: 'CTA section', completed: false },
            { text: 'Responsive breakpoints', completed: false },
        ],
        timeLog: [],
        comments: [],
    },
    {
        id: 'TASK-005',
        projectId: 'PRJ-001',
        title: 'Set up CI/CD pipeline',
        status: 'done',
        priority: 'medium',
        assignee: '@carlos',
        storyPoints: 3,
        labels: ['devops'],
        created: '2026-06-01T11:00:00Z',
        updated: '2026-06-02T18:00:00Z',
        lastUpdatedBy: 'carlos',
        started: '2026-06-01T11:30:00Z',
        description: 'Set up GitHub Actions for automated testing, linting, and deployment to staging.',
        acceptanceCriteria: [
            { text: 'Test automation', completed: true },
            { text: 'Lint checks', completed: true },
            { text: 'Build verification', completed: true },
            { text: 'Deploy to staging', completed: true },
            { text: 'PR preview deployments', completed: true },
        ],
        timeLog: [],
        comments: [],
    },
    {
        id: 'TASK-006',
        projectId: 'PRJ-001',
        title: 'Optimize image loading',
        status: 'to-do',
        priority: 'medium',
        storyPoints: 2,
        labels: ['performance'],
        created: '2026-06-08T09:00:00Z',
        updated: '2026-06-08T09:00:00Z',
        description: 'Implement lazy loading, WebP format, and responsive images for optimal performance.',
        acceptanceCriteria: [
            { text: 'Lazy loading below fold', completed: false },
            { text: 'WebP with fallback', completed: false },
            { text: 'Responsive srcset', completed: false },
            { text: 'Blur placeholder', completed: false },
        ],
        timeLog: [],
        comments: [],
    },
    {
        id: 'TASK-007',
        projectId: 'PRJ-001',
        title: 'Implement dark mode toggle',
        status: 'backlog',
        priority: 'low',
        storyPoints: 3,
        labels: ['ui', 'theming'],
        created: '2026-06-09T09:00:00Z',
        updated: '2026-06-09T09:00:00Z',
        description: 'Add dark mode toggle with system preference detection and local storage persistence.',
        acceptanceCriteria: [
            { text: 'Toggle in header', completed: false },
            { text: 'System preference detection', completed: false },
            { text: 'Local storage persistence', completed: false },
            { text: 'Smooth transition', completed: false },
        ],
        timeLog: [],
        comments: [],
    },
    {
        id: 'TASK-008',
        projectId: 'PRJ-001',
        title: 'Add analytics tracking',
        status: 'backlog',
        priority: 'medium',
        storyPoints: 2,
        labels: ['analytics'],
        created: '2026-06-09T10:00:00Z',
        updated: '2026-06-09T10:00:00Z',
        description: 'Integrate analytics tracking for page views, events, and conversions.',
        acceptanceCriteria: [
            { text: 'Page view tracking', completed: false },
            { text: 'Event tracking', completed: false },
            { text: 'Conversion tracking', completed: false },
        ],
        timeLog: [],
        comments: [],
    },
    {
        id: 'TASK-009',
        projectId: 'PRJ-001',
        title: 'Create 404 error page',
        status: 'to-do',
        priority: 'low',
        storyPoints: 1,
        labels: ['ui'],
        created: '2026-06-10T09:00:00Z',
        updated: '2026-06-10T09:00:00Z',
        description: 'Design and implement a friendly 404 error page with navigation back to home.',
        acceptanceCriteria: [
            { text: 'Friendly design', completed: false },
            { text: 'Search functionality', completed: false },
            { text: 'Navigation links', completed: false },
        ],
        timeLog: [],
        comments: [],
    },
    {
        id: 'TASK-010',
        projectId: 'PRJ-001',
        title: 'Implement search functionality',
        status: 'in-review',
        priority: 'high',
        assignee: '@alex',
        storyPoints: 5,
        labels: ['frontend', 'search'],
        created: '2026-06-04T10:00:00Z',
        updated: '2026-06-11T11:00:00Z',
        lastUpdatedBy: 'alex',
        started: '2026-06-04T10:30:00Z',
        description: 'Full-text search across all pages with instant results and keyboard navigation.',
        acceptanceCriteria: [
            { text: 'Search input in header', completed: true },
            { text: 'Instant results dropdown', completed: true },
            { text: 'Keyboard navigation', completed: true },
            { text: 'Search highlighting', completed: true },
            { text: 'Empty state', completed: false },
        ],
        timeLog: [],
        comments: [],
    },
    {
        id: 'TASK-011',
        projectId: 'PRJ-001',
        title: 'Set up error monitoring',
        status: 'done',
        priority: 'medium',
        assignee: '@carlos',
        storyPoints: 2,
        labels: ['devops', 'monitoring'],
        created: '2026-06-03T09:00:00Z',
        updated: '2026-06-03T17:00:00Z',
        lastUpdatedBy: 'carlos',
        started: '2026-06-03T09:30:00Z',
        description: 'Set up Sentry for error tracking and performance monitoring.',
        acceptanceCriteria: [
            { text: 'Sentry integration', completed: true },
            { text: 'Source maps upload', completed: true },
            { text: 'Alert configuration', completed: true },
        ],
        timeLog: [],
        comments: [],
    },
    {
        id: 'TASK-012',
        projectId: 'PRJ-001',
        title: 'Create about page',
        status: 'to-do',
        priority: 'medium',
        assignee: '@maya',
        storyPoints: 3,
        labels: ['design', 'content'],
        created: '2026-06-10T10:00:00Z',
        updated: '2026-06-10T10:00:00Z',
        lastUpdatedBy: 'maya',
        description: 'Design and implement the about page with team information and company story.',
        acceptanceCriteria: [
            { text: 'Team section', completed: false },
            { text: 'Company story', completed: false },
            { text: 'Values section', completed: false },
            { text: 'Contact information', completed: false },
        ],
        timeLog: [],
        comments: [],
    },
    {
        id: 'TASK-013',
        projectId: 'PRJ-001',
        title: 'Implement contact form',
        status: 'in-progress',
        priority: 'high',
        assignee: '@carlos',
        storyPoints: 3,
        labels: ['backend', 'forms'],
        created: '2026-06-07T09:00:00Z',
        updated: '2026-06-11T10:00:00Z',
        lastUpdatedBy: 'carlos',
        started: '2026-06-07T09:30:00Z',
        description: 'Contact form with validation, spam protection, and email notification.',
        acceptanceCriteria: [
            { text: 'Form validation', completed: true },
            { text: 'Email validation', completed: true },
            { text: 'reCAPTCHA integration', completed: false },
            { text: 'Email notification', completed: false },
            { text: 'Success/error states', completed: false },
        ],
        timeLog: [],
        comments: [],
    },
    {
        id: 'TASK-014',
        projectId: 'PRJ-001',
        title: 'Add loading animations',
        status: 'backlog',
        priority: 'low',
        storyPoints: 2,
        labels: ['ui', 'animation'],
        created: '2026-06-10T11:00:00Z',
        updated: '2026-06-10T11:00:00Z',
        description: 'Add skeleton loaders and transition animations for better UX.',
        acceptanceCriteria: [
            { text: 'Skeleton loaders', completed: false },
            { text: 'Page transitions', completed: false },
            { text: 'Micro-interactions', completed: false },
        ],
        timeLog: [],
        comments: [],
    },
    {
        id: 'TASK-015',
        projectId: 'PRJ-001',
        title: 'Cross-browser testing',
        status: 'to-do',
        priority: 'high',
        assignee: '@sarah',
        storyPoints: 3,
        labels: ['testing', 'qa'],
        created: '2026-06-10T09:00:00Z',
        updated: '2026-06-10T09:00:00Z',
        lastUpdatedBy: 'sarah',
        description: 'Test across Chrome, Firefox, Safari, and Edge. Document any compatibility issues.',
        acceptanceCriteria: [
            { text: 'Chrome testing', completed: false },
            { text: 'Firefox testing', completed: false },
            { text: 'Safari testing', completed: false },
            { text: 'Edge testing', completed: false },
            { text: 'Mobile browsers', completed: false },
        ],
        timeLog: [],
        comments: [],
    },
    // PRJ-002 Scrum tasks
    {
        id: 'TASK-101',
        projectId: 'PRJ-002',
        title: 'Initialize Capacitor project',
        status: 'done',
        priority: 'high',
        assignee: '@admin',
        storyPoints: 3,
        sprint: 'sprint-1',
        labels: ['setup', 'mobile'],
        created: '2026-06-02T09:00:00Z',
        updated: '2026-06-03T17:00:00Z',
        lastUpdatedBy: 'admin',
        started: '2026-06-02T09:30:00Z',
        description: 'Initialize Capacitor project with React, TypeScript, and basic folder structure.',
        acceptanceCriteria: [
            { text: 'Capacitor project created', completed: true },
            { text: 'React + TypeScript configured', completed: true },
            { text: 'Basic folder structure', completed: true },
            { text: 'Build runs successfully', completed: true },
            { text: 'App launches on iOS simulator', completed: true },
        ],
        timeLog: [],
        comments: [],
    },
    {
        id: 'TASK-102',
        projectId: 'PRJ-002',
        title: 'Set up iOS build environment',
        status: 'done',
        priority: 'high',
        assignee: '@alex',
        storyPoints: 2,
        sprint: 'sprint-1',
        labels: ['setup', 'ios'],
        created: '2026-06-02T09:00:00Z',
        updated: '2026-06-02T17:00:00Z',
        lastUpdatedBy: 'alex',
        started: '2026-06-02T09:30:00Z',
        description: 'Configure Xcode project, provisioning profiles, and build settings.',
        acceptanceCriteria: [
            { text: 'Xcode project configured', completed: true },
            { text: 'Provisioning profile set', completed: true },
            { text: 'Debug build works', completed: true },
        ],
        timeLog: [],
        comments: [],
    },
    {
        id: 'TASK-103',
        projectId: 'PRJ-002',
        title: 'Set up Android build environment',
        status: 'done',
        priority: 'high',
        assignee: '@alex',
        storyPoints: 2,
        sprint: 'sprint-1',
        labels: ['setup', 'android'],
        created: '2026-06-02T09:00:00Z',
        updated: '2026-06-02T17:00:00Z',
        lastUpdatedBy: 'alex',
        started: '2026-06-02T09:30:00Z',
        description: 'Configure Android Studio project, signing, and build settings.',
        acceptanceCriteria: [
            { text: 'Android Studio project configured', completed: true },
            { text: 'Signing configured', completed: true },
            { text: 'Debug build works', completed: true },
        ],
        timeLog: [],
        comments: [],
    },
    {
        id: 'TASK-104',
        projectId: 'PRJ-002',
        title: 'Design app wireframes',
        status: 'done',
        priority: 'high',
        assignee: '@maya',
        storyPoints: 3,
        sprint: 'sprint-1',
        labels: ['design', 'mobile'],
        created: '2026-06-02T09:00:00Z',
        updated: '2026-06-05T17:00:00Z',
        lastUpdatedBy: 'maya',
        started: '2026-06-02T09:30:00Z',
        description: 'Create wireframes for all main screens: login, project list, task list, task detail.',
        acceptanceCriteria: [
            { text: 'Login/Register screens', completed: true },
            { text: 'Project list screen', completed: true },
            { text: 'Task list screen', completed: true },
            { text: 'Task detail screen', completed: true },
            { text: 'Settings screen', completed: true },
        ],
        timeLog: [],
        comments: [],
    },
    {
        id: 'TASK-105',
        projectId: 'PRJ-002',
        title: 'Implement registration screen',
        status: 'done',
        priority: 'high',
        assignee: '@admin',
        storyPoints: 5,
        sprint: 'sprint-1',
        labels: ['auth', 'mobile'],
        created: '2026-06-04T09:00:00Z',
        updated: '2026-06-08T17:00:00Z',
        lastUpdatedBy: 'admin',
        started: '2026-06-04T09:30:00Z',
        description: 'User registration with email, password, and confirmation.',
        acceptanceCriteria: [
            { text: 'Email input with validation', completed: true },
            { text: 'Password input with strength indicator', completed: true },
            { text: 'Confirm password field', completed: true },
            { text: 'Submit button with loading state', completed: true },
            { text: 'Error message display', completed: true },
            { text: 'Success redirect to login', completed: true },
        ],
        timeLog: [],
        comments: [],
    },
    {
        id: 'TASK-106',
        projectId: 'PRJ-002',
        title: 'Implement login screen',
        status: 'done',
        priority: 'high',
        assignee: '@admin',
        storyPoints: 5,
        sprint: 'sprint-1',
        labels: ['auth', 'mobile'],
        created: '2026-06-04T09:00:00Z',
        updated: '2026-06-08T17:00:00Z',
        lastUpdatedBy: 'admin',
        started: '2026-06-04T09:30:00Z',
        description: 'User login with email and password, remember me option.',
        acceptanceCriteria: [
            { text: 'Email input', completed: true },
            { text: 'Password input', completed: true },
            { text: 'Remember me checkbox', completed: true },
            { text: 'Forgot password link', completed: true },
            { text: 'Error handling', completed: true },
            { text: 'Success redirect', completed: true },
        ],
        timeLog: [],
        comments: [],
    },
    {
        id: 'TASK-107',
        projectId: 'PRJ-002',
        title: 'Create project list screen',
        status: 'done',
        priority: 'high',
        assignee: '@admin',
        storyPoints: 3,
        sprint: 'sprint-2',
        labels: ['projects', 'mobile'],
        created: '2026-06-16T09:00:00Z',
        updated: '2026-06-18T17:00:00Z',
        started: '2026-06-16T09:30:00Z',
        description: 'Display list of user projects with search and filter.',
        acceptanceCriteria: [
            { text: 'Project cards', completed: true },
            { text: 'Search functionality', completed: true },
            { text: 'Pull to refresh', completed: true },
            { text: 'Empty state', completed: true },
        ],
        timeLog: [],
        comments: [],
    },
    {
        id: 'TASK-108',
        projectId: 'PRJ-002',
        title: 'Create project detail screen',
        status: 'done',
        priority: 'high',
        assignee: '@admin',
        storyPoints: 3,
        sprint: 'sprint-2',
        labels: ['projects', 'mobile'],
        created: '2026-06-16T09:00:00Z',
        updated: '2026-06-19T17:00:00Z',
        started: '2026-06-16T09:30:00Z',
        description: 'Display project details with tasks, team, and settings.',
        acceptanceCriteria: [
            { text: 'Project header', completed: true },
            { text: 'Task list', completed: true },
            { text: 'Team members', completed: true },
            { text: 'Settings access', completed: true },
        ],
        timeLog: [],
        comments: [],
    },
    {
        id: 'TASK-109',
        projectId: 'PRJ-002',
        title: 'Create task list screen',
        status: 'done',
        priority: 'high',
        assignee: '@admin',
        storyPoints: 2,
        sprint: 'sprint-2',
        labels: ['tasks', 'mobile'],
        created: '2026-06-16T09:00:00Z',
        updated: '2026-06-17T17:00:00Z',
        started: '2026-06-16T09:30:00Z',
        description: 'Display task list with status indicators and quick actions.',
        acceptanceCriteria: [
            { text: 'Task cards', completed: true },
            { text: 'Status badges', completed: true },
            { text: 'Priority indicators', completed: true },
            { text: 'Swipe actions', completed: true },
        ],
        timeLog: [],
        comments: [],
    },
    {
        id: 'TASK-110',
        projectId: 'PRJ-002',
        title: 'Create task detail screen',
        status: 'in-progress',
        priority: 'high',
        assignee: '@admin',
        storyPoints: 5,
        sprint: 'sprint-2',
        labels: ['tasks', 'mobile'],
        created: '2026-06-16T09:00:00Z',
        updated: '2026-06-20T14:00:00Z',
        started: '2026-06-16T09:30:00Z',
        description: 'Display task details with all fields, allow editing, and show activity timeline.',
        acceptanceCriteria: [
            { text: 'Task title and description', completed: true },
            { text: 'Status selector', completed: true },
            { text: 'Priority selector', completed: false },
            { text: 'Assignee selector', completed: false },
            { text: 'Labels management', completed: false },
            { text: 'Comments section', completed: false },
            { text: 'Time log section', completed: false },
        ],
        timeLog: [],
        comments: [],
    },
    {
        id: 'TASK-111',
        projectId: 'PRJ-002',
        title: 'Implement task creation',
        status: 'in-progress',
        priority: 'high',
        assignee: '@admin',
        storyPoints: 5,
        sprint: 'sprint-2',
        labels: ['tasks', 'mobile'],
        created: '2026-06-18T09:00:00Z',
        updated: '2026-06-20T14:00:00Z',
        started: '2026-06-18T09:30:00Z',
        description: 'Create new task with title, description, priority, and assignee.',
        acceptanceCriteria: [
            { text: 'Task form', completed: true },
            { text: 'Title input', completed: true },
            { text: 'Description textarea', completed: true },
            { text: 'Priority picker', completed: false },
            { text: 'Assignee picker', completed: false },
        ],
        timeLog: [],
        comments: [],
    },
    {
        id: 'TASK-112',
        projectId: 'PRJ-002',
        title: 'Implement task status update',
        status: 'to-do',
        priority: 'medium',
        assignee: '@admin',
        storyPoints: 3,
        sprint: 'sprint-2',
        labels: ['tasks', 'mobile'],
        created: '2026-06-18T09:00:00Z',
        updated: '2026-06-18T09:00:00Z',
        description: 'Quick status change with swipe gestures.',
        acceptanceCriteria: [
            { text: 'Swipe to change status', completed: false },
            { text: 'Status picker modal', completed: false },
            { text: 'Optimistic updates', completed: false },
        ],
        timeLog: [],
        comments: [],
    },
    {
        id: 'TASK-113',
        projectId: 'PRJ-002',
        title: 'Design task card component',
        status: 'done',
        priority: 'medium',
        assignee: '@maya',
        storyPoints: 3,
        sprint: 'sprint-2',
        labels: ['design', 'components'],
        created: '2026-06-16T09:00:00Z',
        updated: '2026-06-17T17:00:00Z',
        started: '2026-06-16T09:30:00Z',
        description: 'Design reusable task card component for mobile.',
        acceptanceCriteria: [
            { text: 'Card layout', completed: true },
            { text: 'Status badge', completed: true },
            { text: 'Priority indicator', completed: true },
            { text: 'Assignee avatar', completed: true },
            { text: 'Touch feedback', completed: true },
        ],
        timeLog: [],
        comments: [],
    },
    {
        id: 'TASK-114',
        projectId: 'PRJ-002',
        title: 'Set up backend API',
        status: 'in-progress',
        priority: 'high',
        assignee: '@carlos',
        storyPoints: 5,
        sprint: 'sprint-2',
        labels: ['backend', 'api'],
        created: '2026-06-16T09:00:00Z',
        updated: '2026-06-20T16:00:00Z',
        started: '2026-06-16T09:30:00Z',
        description: 'Set up Express.js backend with authentication endpoints, project CRUD, and task CRUD.',
        acceptanceCriteria: [
            { text: 'Express server setup', completed: true },
            { text: 'PostgreSQL database', completed: true },
            { text: 'Authentication endpoints', completed: true },
            { text: 'Project CRUD endpoints', completed: true },
            { text: 'Task CRUD endpoints', completed: false },
            { text: 'API documentation', completed: false },
        ],
        timeLog: [],
        comments: [],
    },
];
const seedSprints = [
    {
        id: 'sprint-1',
        projectId: 'PRJ-002',
        name: 'Sprint 1: Foundation',
        startDate: '2026-06-02',
        endDate: '2026-06-15',
        status: 'completed',
        goal: 'Set up project foundation with authentication',
        committedPoints: 21,
        completedPoints: 18,
        velocity: 18,
        committedTasks: ['TASK-101', 'TASK-102', 'TASK-103', 'TASK-104', 'TASK-105', 'TASK-106'],
    },
    {
        id: 'sprint-2',
        projectId: 'PRJ-002',
        name: 'Sprint 2: Core Features',
        startDate: '2026-06-16',
        endDate: '2026-06-29',
        status: 'in-progress',
        goal: 'Implement project and task management',
        committedPoints: 26,
        completedPoints: 8,
        velocity: 0,
        committedTasks: ['TASK-107', 'TASK-108', 'TASK-109', 'TASK-110', 'TASK-111', 'TASK-112', 'TASK-113', 'TASK-114'],
    },
    {
        id: 'sprint-3',
        projectId: 'PRJ-002',
        name: 'Sprint 3: Board Views',
        startDate: '2026-06-30',
        endDate: '2026-07-13',
        status: 'planned',
        goal: 'Implement Kanban and Sprint boards',
        committedPoints: 0,
        completedPoints: 0,
        velocity: 0,
        committedTasks: [],
    },
];
const seedEpics = [
    {
        id: 'EPIC-001',
        projectId: 'PRJ-002',
        title: 'Authentication',
        priority: 'high',
        status: 'in-progress',
        storyPoints: 21,
        userStories: [
            { id: 'US-001', description: 'User can register with email and password', completed: true },
            { id: 'US-002', description: 'User can log in with credentials', completed: true },
            { id: 'US-003', description: 'User can log out', completed: true },
            { id: 'US-004', description: 'User can reset password via email', completed: false },
            { id: 'US-005', description: 'User can sign in with Google', completed: false },
            { id: 'US-006', description: 'User can sign in with Apple', completed: false },
        ],
    },
    {
        id: 'EPIC-002',
        projectId: 'PRJ-002',
        title: 'Project Management',
        priority: 'high',
        status: 'in-progress',
        storyPoints: 30,
        userStories: [
            { id: 'US-007', description: 'User can create a new project', completed: true },
            { id: 'US-008', description: 'User can view project list', completed: true },
            { id: 'US-009', description: 'User can view project details', completed: true },
            { id: 'US-010', description: 'User can edit project settings', completed: false },
            { id: 'US-011', description: 'User can archive a project', completed: false },
            { id: 'US-012', description: 'User can delete a project', completed: false },
            { id: 'US-013', description: 'User can switch between projects', completed: false },
        ],
    },
    {
        id: 'EPIC-003',
        projectId: 'PRJ-002',
        title: 'Task Management',
        priority: 'high',
        status: 'in-progress',
        storyPoints: 42,
        userStories: [
            { id: 'US-014', description: 'User can create a task', completed: true },
            { id: 'US-015', description: 'User can view task list', completed: true },
            { id: 'US-016', description: 'User can view task details', completed: true },
            { id: 'US-017', description: 'User can update task status', completed: true },
            { id: 'US-018', description: 'User can assign a task', completed: false },
            { id: 'US-019', description: 'User can set task priority', completed: false },
            { id: 'US-020', description: 'User can add task labels', completed: false },
            { id: 'US-021', description: 'User can add comments to task', completed: false },
            { id: 'US-022', description: 'User can track time on task', completed: false },
        ],
    },
    {
        id: 'EPIC-004',
        projectId: 'PRJ-002',
        title: 'Board Views',
        priority: 'medium',
        status: 'backlog',
        storyPoints: 25,
        userStories: [
            { id: 'US-023', description: 'User can view Kanban board', completed: false },
            { id: 'US-024', description: 'User can drag and drop tasks', completed: false },
            { id: 'US-025', description: 'User can view Sprint board', completed: false },
            { id: 'US-026', description: 'User can filter board by assignee', completed: false },
            { id: 'US-027', description: 'User can filter board by priority', completed: false },
        ],
    },
    {
        id: 'EPIC-005',
        projectId: 'PRJ-002',
        title: 'Reports & Analytics',
        priority: 'medium',
        status: 'backlog',
        storyPoints: 16,
        userStories: [
            { id: 'US-028', description: 'User can view burndown chart', completed: false },
            { id: 'US-029', description: 'User can view velocity chart', completed: false },
            { id: 'US-030', description: 'User can view cumulative flow', completed: false },
            { id: 'US-031', description: 'User can export reports', completed: false },
        ],
    },
];
async function autoLogChange(projectId, get, set, action, target, details, user) {
    const state = get();
    const sections = state._sections[projectId] || {};
    const existingRaw = sections['change log'] || '';
    const lines = existingRaw.split('\n').filter(l => l.startsWith('|') && !l.startsWith('|---') && !l.startsWith('| #'));
    const nextNumber = lines.length + 1;
    const metadata = await captureMetadata({ captureGeo: false, captureDevice: true, captureNetwork: false, consentGiven: false }, 'system');
    const entryUser = user || '@system';
    const entry = generateChangeLogEntry(nextNumber, entryUser, action, target, details, metadata);
    const entryLine = `| ${entry.number} | ${entry.timestamp} | ${entry.user} | ${entry.action} | ${entry.target} | ${entry.details} |`;
    const newChangeLog = existingRaw ? existingRaw + '\n' + entryLine : entryLine;
    set((s) => ({
        _sections: {
            ...s._sections,
            [projectId]: {
                ...s._sections[projectId],
                'change log': newChangeLog,
            },
        },
    }));
}
function parseDuration(duration) {
    const match = duration.match(/(?:(\d+)h)?\s*(?:(\d+)m)?/);
    if (!match)
        return 0;
    const hours = parseInt(match[1] || '0', 10);
    const minutes = parseInt(match[2] || '0', 10);
    return hours * 60 + minutes;
}
function formatMinutes(total) {
    const h = Math.floor(total / 60);
    const m = total % 60;
    return h > 0 ? `${h}h ${m}m` : `${m}m`;
}
export const useProjectStore = create()(persist((set, get) => ({
    projects: seedProjects,
    currentProjectId: null,
    tasks: seedTasks,
    sprints: seedSprints,
    epics: seedEpics,
    timeLogs: {},
    activeTimer: null,
    _rawContent: {},
    _sections: {},
    pendingChanges: 0,
    resetPendingChanges: () => set({ pendingChanges: 0 }),
    setCurrentProject: (id) => set({ currentProjectId: id }),
    addProject: (project) => set((state) => ({
        projects: [...state.projects, {
                ...project,
                config: { ...project.config, states: ensureDefaultStates(project.config.states) },
            }],
        pendingChanges: state.pendingChanges + 1,
    })),
    updateProject: (id, updates) => set((state) => ({
        projects: state.projects.map((p) => p.project_id === id ? { ...p, ...updates } : p),
    })),
    deleteProject: (id) => set((state) => ({
        projects: state.projects.filter((p) => p.project_id !== id),
        tasks: state.tasks.filter((t) => t.projectId !== id),
        sprints: state.sprints.filter((s) => s.projectId !== id),
        epics: state.epics.filter((e) => e.projectId !== id),
    })),
    updateProjectStates: (id, states) => set((state) => ({
        projects: state.projects.map((p) => p.project_id === id
            ? { ...p, config: { ...p.config, states }, updated: new Date().toISOString() }
            : p),
        pendingChanges: state.pendingChanges + 1,
    })),
    addTask: (task) => {
        const currentUser = useAuthStore.getState().user;
        set((state) => ({
            tasks: [...state.tasks, task],
            pendingChanges: state.pendingChanges + 1,
        }));
        if (task.projectId) {
            autoLogChange(task.projectId, get, set, 'task-created', task.id, task.title, currentUser?.username);
        }
    },
    updateTask: (id, updates) => {
        const prev = get().tasks.find((t) => t.id === id);
        const currentUser = useAuthStore.getState().user;
        set((state) => ({
            tasks: state.tasks.map((t) => t.id === id ? { ...t, ...updates, updated: new Date().toISOString(), lastUpdatedBy: currentUser?.username } : t),
            pendingChanges: state.pendingChanges + 1,
        }));
        if (prev?.projectId) {
            if (updates.status && updates.status !== prev.status) {
                autoLogChange(prev.projectId, get, set, 'status-change', prev.id, `${prev.status} → ${updates.status}`, currentUser?.username);
            }
            if (updates.assignee !== undefined && updates.assignee !== prev.assignee) {
                autoLogChange(prev.projectId, get, set, 'task-assigned', prev.id, `assigned to ${updates.assignee}`, currentUser?.username);
            }
        }
    },
    updateTaskStatus: (id, status) => {
        const prevTask = get().tasks.find(t => t.id === id);
        const currentUser = useAuthStore.getState().user;
        set((state) => ({
            tasks: state.tasks.map((t) => t.id === id ? { ...t, status, updated: new Date().toISOString(), lastUpdatedBy: currentUser?.username } : t),
            pendingChanges: state.pendingChanges + 1,
        }));
        if (prevTask?.projectId && prevTask.status !== status) {
            autoLogChange(prevTask.projectId, get, set, 'status-change', id, `${prevTask.status} -> ${status}`, currentUser?.username);
        }
    },
    deleteTask: (id) => {
        const task = get().tasks.find((t) => t.id === id);
        const currentUser = useAuthStore.getState().user;
        set((state) => ({
            tasks: state.tasks.filter((t) => t.id !== id),
            pendingChanges: state.pendingChanges + 1,
        }));
        if (task?.projectId) {
            autoLogChange(task.projectId, get, set, 'task-deleted', task.id, task.title, currentUser?.username);
        }
    },
    addSprint: (sprint) => {
        const currentUser = useAuthStore.getState().user;
        set((state) => ({
            sprints: [...state.sprints, sprint],
            pendingChanges: state.pendingChanges + 1,
        }));
        if (sprint.projectId) {
            autoLogChange(sprint.projectId, get, set, 'sprint-created', sprint.name, `${sprint.startDate} to ${sprint.endDate}`, currentUser?.username);
        }
    },
    updateSprint: (id, updates) => {
        const prev = get().sprints.find((s) => s.id === id);
        const currentUser = useAuthStore.getState().user;
        set((state) => ({
            sprints: state.sprints.map((s) => s.id === id ? { ...s, ...updates } : s),
            pendingChanges: state.pendingChanges + 1,
        }));
        if (prev?.projectId && updates.status === 'completed' && prev.status !== 'completed') {
            autoLogChange(prev.projectId, get, set, 'sprint-completed', prev.name, `completed with ${prev.completedPoints} points`, currentUser?.username);
        }
    },
    deleteSprint: (id) => set((state) => ({
        sprints: state.sprints.filter((s) => s.id !== id),
        pendingChanges: state.pendingChanges + 1,
    })),
    addEpic: (epic) => set((state) => ({
        epics: [...state.epics, epic],
        pendingChanges: state.pendingChanges + 1,
    })),
    updateEpic: (id, updates) => set((state) => ({
        epics: state.epics.map((e) => e.id === id ? { ...e, ...updates } : e),
        pendingChanges: state.pendingChanges + 1,
    })),
    deleteEpic: (id) => set((state) => ({
        epics: state.epics.filter((e) => e.id !== id),
        pendingChanges: state.pendingChanges + 1,
    })),
    getTasksByProject: (projectId) => {
        return get().tasks.filter((t) => t.projectId === projectId);
    },
    addComment: (taskId, comment) => set((state) => ({
        tasks: state.tasks.map((t) => t.id === taskId ? { ...t, comments: [...t.comments, comment] } : t),
        pendingChanges: state.pendingChanges + 1,
    })),
    addTimeLog: (taskId, entry) => {
        set((state) => {
            const existing = state.timeLogs[taskId];
            const entryMinutes = parseDuration(entry.duration);
            if (existing) {
                return {
                    timeLogs: {
                        ...state.timeLogs,
                        [taskId]: {
                            ...existing,
                            entries: [...existing.entries, entry],
                            totalDuration: existing.totalDuration + entryMinutes,
                        },
                    },
                    pendingChanges: state.pendingChanges + 1,
                };
            }
            return {
                timeLogs: {
                    ...state.timeLogs,
                    [taskId]: {
                        id: `TL-${Date.now()}`,
                        taskId,
                        entries: [entry],
                        totalDuration: entryMinutes,
                    },
                },
                pendingChanges: state.pendingChanges + 1,
            };
        });
        const task = get().tasks.find((t) => t.id === taskId);
        const currentUser = useAuthStore.getState().user;
        if (task?.projectId) {
            autoLogChange(task.projectId, get, set, 'time-log', taskId, entry.duration, currentUser?.username);
        }
    },
    startTimer: (taskId) => {
        set({
            activeTimer: {
                taskId,
                startTime: new Date().toISOString(),
                isRunning: true,
                elapsed: 0,
            },
        });
    },
    stopTimer: () => {
        const { activeTimer, timeLogs } = get();
        if (!activeTimer || !activeTimer.isRunning)
            return;
        const elapsed = Math.round((Date.now() - new Date(activeTimer.startTime).getTime()) / 60000);
        const taskId = activeTimer.taskId;
        const entry = {
            date: new Date().toISOString(),
            user: '@user',
            duration: formatMinutes(elapsed),
            description: '',
        };
        const existing = timeLogs[taskId];
        if (existing) {
            set((state) => ({
                activeTimer: null,
                timeLogs: {
                    ...state.timeLogs,
                    [taskId]: {
                        ...existing,
                        entries: [...existing.entries, entry],
                        totalDuration: existing.totalDuration + elapsed,
                    },
                },
            }));
        }
        else {
            set((state) => ({
                activeTimer: null,
                timeLogs: {
                    ...state.timeLogs,
                    [taskId]: {
                        id: `TL-${Date.now()}`,
                        taskId,
                        entries: [entry],
                        totalDuration: elapsed,
                    },
                },
            }));
        }
    },
    getTimeLog: (taskId) => get().timeLogs[taskId],
    setSection: (projectId, sectionKey, value) => set((state) => ({
        _sections: {
            ...state._sections,
            [projectId]: {
                ...state._sections[projectId],
                [sectionKey]: value,
            },
        },
        pendingChanges: state.pendingChanges + 1,
    })),
    loadSeedData: () => {
        set({
            projects: seedProjects,
            tasks: seedTasks,
            sprints: seedSprints,
            epics: seedEpics,
            timeLogs: {},
            activeTimer: null,
            currentProjectId: null,
            _rawContent: {},
            _sections: {},
            pendingChanges: 0,
        });
    },
    syncFromGit: (files) => {
        const existingProjects = get().projects;
        const existingMap = new Map(existingProjects.map((p) => [p.project_id, p]));
        const projects = files.map((f) => {
            const id = f.replace(/\.md$/, '');
            const existing = existingMap.get(id);
            if (existing)
                return existing;
            return {
                project_id: id,
                name: id,
                workflow: 'kanban',
                default_branch: 'main',
                created: new Date().toISOString(),
                updated: new Date().toISOString(),
                status: 'active',
                config: { states: ensureDefaultStates() },
            };
        });
        set({
            projects,
            tasks: [],
            sprints: [],
            epics: [],
            timeLogs: {},
            activeTimer: null,
            currentProjectId: null,
            _rawContent: {},
            _sections: {},
            pendingChanges: 0,
        });
    },
    loadProjectContent: async (id, client) => {
        const api = createProjectsApi(client);
        const res = await api.get(id);
        const parsed = parseProjectFile(res.content);
        const sections = {};
        for (const [key, val] of parsed.sections) {
            sections[key.toLowerCase()] = val;
        }
        const projectMeta = {
            ...parsed.frontMatter,
            project_id: id,
            name: parsed.frontMatter.name || id,
        };
        // Parse tasks/sprints from section content
        const backlogTasks = parseBacklogKanban(sections.backlog || '');
        const detailTasks = parseTasks(sections.tasks || '');
        const mergedTasks = detailTasks.length > 0 ? detailTasks : backlogTasks;
        const projectSprints = parseSprints(sections.sprints || '');
        set((state) => {
            const existing = state.projects.find((p) => p.project_id === id);
            const projects = existing
                ? state.projects.map((p) => (p.project_id === id ? projectMeta : p))
                : [...state.projects, projectMeta];
            // Remove old tasks for this project, add parsed ones
            const otherTasks = state.tasks.filter((t) => t.projectId !== id);
            const otherSprints = state.sprints.filter((s) => s.projectId !== id);
            return {
                projects,
                currentProjectId: id,
                _rawContent: { ...state._rawContent, [id]: res.content },
                _sections: { ...state._sections, [id]: sections },
                tasks: [...otherTasks, ...mergedTasks.map((t) => ({ ...t, projectId: id }))],
                sprints: [...otherSprints, ...projectSprints.map((s) => ({ ...s, projectId: id }))],
            };
        });
    },
    loadAllProjectSections: async (client) => {
        const api = createProjectsApi(client);
        const state = get();
        const projects = state.projects;
        const newSections = { ...state._sections };
        for (const project of projects) {
            if (newSections[project.project_id]?.team)
                continue;
            try {
                const res = await api.get(project.project_id);
                const parsed = parseProjectFile(res.content);
                const sections = {};
                for (const [key, val] of parsed.sections) {
                    sections[key.toLowerCase()] = val;
                }
                newSections[project.project_id] = sections;
            }
            catch {
                // skip projects that fail to load
            }
        }
        set({ _sections: newSections });
    },
    saveProjectContent: async (id, client, message) => {
        const state = get();
        const project = state.projects.find((p) => p.project_id === id);
        if (!project)
            return;
        const sections = state._sections[id] || {};
        const projectTasks = state.tasks.filter((t) => t.projectId === id);
        const projectEpics = state.epics.filter((e) => e.projectId === id);
        const lines = [];
        lines.push(serializeFrontMatter(project));
        lines.push('');
        lines.push('## Overview');
        lines.push('');
        lines.push(sections.overview || '');
        lines.push('');
        lines.push('## Team');
        lines.push('');
        lines.push(sections.team || '');
        lines.push('');
        if (projectEpics.length > 0) {
            lines.push('## Backlog');
            lines.push('');
            lines.push(serializeBacklog(projectTasks));
            lines.push('');
        }
        const projectSprints = state.sprints.filter((s) => s.projectId === id);
        if (projectSprints.length > 0) {
            lines.push('## Sprints');
            lines.push('');
            lines.push(serializeSprints(projectSprints));
            lines.push('');
        }
        lines.push('## Tasks');
        lines.push('');
        for (const task of projectTasks) {
            lines.push(serializeTask(task));
            lines.push('');
        }
        lines.push('## Reports');
        lines.push('');
        lines.push(sections.reports || '');
        lines.push('');
        lines.push('## Change Log');
        lines.push('');
        lines.push(sections['change log'] || '');
        lines.push('');
        const content = lines.join('\n');
        const api = createProjectsApi(client);
        try {
            if (state._rawContent[id]) {
                await api.update(id, content, message);
            }
            else {
                await api.create(id, content, message);
            }
            set((state) => ({
                _rawContent: { ...state._rawContent, [id]: content },
            }));
        }
        catch {
            console.error('Failed to save project content to server');
        }
    },
}), {
    name: 'projectmanmd-project',
    onRehydrateStorage: () => {
        return (state) => {
            if (!state)
                return;
            state.projects = state.projects.map((p) => ({
                ...p,
                config: { ...p.config, states: ensureDefaultStates(p.config.states) },
            }));
            state.tasks = state.tasks.map((t) => ({
                ...t,
                lastUpdatedBy: t.lastUpdatedBy || (t.assignee ? t.assignee.replace('@', '') : undefined),
            }));
        };
    },
}));
//# sourceMappingURL=project-store.js.map